/**
 * l51-size-map.js — traducción de códigos maestro a tallas visibles.
 *
 * El option_value que existe en Shopify es el código maestro ("2W4", "W6,5",
 * "J3"). Lo que el cliente ve es su equivalencia en CL, US o Cm según la tabla
 * (`grid`) que resuelve snippets/l51-size-system.liquid. Este módulo hace esa
 * traducción y nada más: sin dependencias, sin DOM, sin eventos. Funciones
 * puras, testeables en node (theme-tests/l51-size-map.test.js).
 *
 * Regla de oro: si falta la equivalencia, se muestra el código maestro crudo.
 * Nunca un string vacío. Con `grid` null todo se comporta como si el módulo no
 * existiera.
 *
 * Gemelo de app/lib/grid.ts en la app crocs-tallas: si cambia una regla acá,
 * cambia allá.
 */
(function (global) {
  'use strict';

  /**
   * Clave de comparación entre un option_value y un código maestro: trim,
   * mayúsculas, sin espacios, coma → punto. "w6,5" === "W6.5" === " W 6.5 ".
   */
  function norm(value) {
    return String(value == null ? '' : value)
      .trim()
      .toUpperCase()
      .replace(/\s+/g, '')
      .replace(/,/g, '.');
  }

  function rawMaster(master) {
    return String(master == null ? '' : master);
  }

  /** Índice de filas por código maestro normalizado, cacheado por grilla. */
  var indexCache = typeof WeakMap === 'function' ? new WeakMap() : null;

  function rowIndex(grid) {
    if (indexCache && indexCache.has(grid)) return indexCache.get(grid);

    var index = Object.create(null);
    (grid.rows || []).forEach(function (row) {
      if (!row) return;
      var key = norm(row.master);
      if (key && !index[key]) index[key] = row;
    });

    if (indexCache) indexCache.set(grid, index);
    return index;
  }

  function findRow(grid, master) {
    if (!grid) return null;
    return rowIndex(grid)[norm(master)] || null;
  }

  function findColumn(grid, key) {
    var columns = (grid && grid.columns) || [];
    for (var i = 0; i < columns.length; i++) {
      if (columns[i] && columns[i].key === key) return columns[i];
    }
    return null;
  }

  function isSubset(grid) {
    return !!(grid && grid.segments && grid.segments.mode === 'subset');
  }

  /**
   * Lee la grilla impresa por el snippet dentro de un scope (la PDP, una
   * card...). Devuelve null si no hay o si el JSON no parsea.
   */
  function readGrid(scope) {
    var root = scope && typeof scope.querySelector === 'function' ? scope : null;
    var node = root ? root.querySelector('[data-l51-size-system]') : null;

    if (!node && typeof document !== 'undefined') {
      node = document.querySelector('[data-l51-size-system]');
    }
    if (!node) return null;

    try {
      var parsed = JSON.parse(node.textContent || '');
      return parsed && typeof parsed === 'object' ? parsed : null;
    } catch (e) {
      return null;
    }
  }

  /**
   * Valor a mostrar para un código maestro.
   * @param {string} master   option_value de la variante
   * @param {Object|null} grid
   * @param {string} [column]  key de columna; por defecto grid.defaultColumn
   * @param {string} [segment] key de segmento; solo importa en columnas segmentadas
   * @returns {string} la equivalencia (con sufijo) o el código maestro crudo
   */
  function display(master, grid, column, segment) {
    var raw = rawMaster(master);
    if (!grid) return raw;

    var row = findRow(grid, master);
    if (!row) return raw;

    var key = column || grid.defaultColumn;
    var col = findColumn(grid, key);
    if (!col) return raw;

    var cell = row[key];
    var value;

    if (cell && typeof cell === 'object') {
      value = segment != null ? cell[segment] : undefined;
      if ((value == null || value === '') && grid.segments && grid.segments.options) {
        // Sin segmento válido: no adivinamos entre Mujer y Hombre.
        return raw;
      }
    } else {
      value = cell;
    }

    if (value == null || String(value).trim() === '') return raw;

    return String(value) + (col.suffix || '');
  }

  /**
   * Variantes que corresponden al segmento activo.
   * En "scale" (UNISEX) devuelve todas: el toggle cambia valores, no filas.
   * En "subset" (NIÑOS) filtra por `row.segment`.
   * @param {Array<{master:string}>} variants
   */
  function visibleVariants(variants, grid, segment) {
    var list = Array.isArray(variants) ? variants : [];
    if (!isSubset(grid)) return list;

    var allowed = Object.create(null);
    (grid.rows || []).forEach(function (row) {
      if (row && row.segment === segment) allowed[norm(row.master)] = true;
    });

    return list.filter(function (variant) {
      return !!allowed[norm(variant && variant.master)];
    });
  }

  /**
   * Cuántas variantes del producto caen en cada segmento.
   * @returns {Object} { [segmentKey]: { total, available } }
   */
  function segmentCounts(variants, grid) {
    var counts = {};
    if (!grid || !grid.segments || !grid.segments.options) return counts;

    grid.segments.options.forEach(function (option) {
      var matching = visibleVariants(variants, grid, option.key);
      counts[option.key] = {
        total: matching.length,
        available: matching.filter(function (v) {
          return !!(v && v.available);
        }).length
      };
    });

    return counts;
  }

  /**
   * Segmento con el que abrir el selector.
   * En "scale", la primera opción declarada. En "subset", el segmento con más
   * variantes disponibles del producto (desempate: más variantes totales, y
   * luego el orden declarado), para que un producto que solo trae J1–J6 no
   * abra en la pestaña "Pequeños" vacía.
   * @returns {string|null}
   */
  function initialSegment(variants, grid) {
    if (!grid || !grid.segments) return null;

    var options = grid.segments.options || [];
    if (!options.length) return null;

    if (!isSubset(grid)) return options[0].key;

    var counts = segmentCounts(variants, grid);
    var best = options[0].key;

    for (var i = 1; i < options.length; i++) {
      var key = options[i].key;
      var a = counts[key] || { total: 0, available: 0 };
      var b = counts[best] || { total: 0, available: 0 };
      if (a.available > b.available || (a.available === b.available && a.total > b.total)) {
        best = key;
      }
    }

    return best;
  }

  /** Columnas sin `hidden`, en orden, para el toggle CL / US / Cm. */
  function visibleColumns(grid) {
    if (!grid || !Array.isArray(grid.columns)) return [];
    return grid.columns.filter(function (column) {
      return column && !column.hidden;
    });
  }

  var STORAGE_KEY = 'l51:size-column';

  /**
   * Escala preferida por el usuario en esta sesión, si sigue siendo una columna
   * visible de la grilla; si no, la columna por defecto; si no, la primera.
   */
  function rememberedColumn(grid) {
    var keys = visibleColumns(grid).map(function (c) {
      return c.key;
    });
    if (!keys.length) return null;

    try {
      var stored = global.sessionStorage ? global.sessionStorage.getItem(STORAGE_KEY) : null;
      if (stored && keys.indexOf(stored) !== -1) return stored;
    } catch (e) {
      /* sessionStorage bloqueado: se usa el default */
    }

    return grid.defaultColumn && keys.indexOf(grid.defaultColumn) !== -1 ? grid.defaultColumn : keys[0];
  }

  function rememberColumn(key) {
    try {
      if (global.sessionStorage) global.sessionStorage.setItem(STORAGE_KEY, String(key));
    } catch (e) {
      /* no-op */
    }
  }

  global.L51SizeMap = {
    norm: norm,
    readGrid: readGrid,
    display: display,
    visibleVariants: visibleVariants,
    initialSegment: initialSegment,
    segmentCounts: segmentCounts,
    visibleColumns: visibleColumns,
    rememberedColumn: rememberedColumn,
    rememberColumn: rememberColumn
  };
})(typeof window !== 'undefined' ? window : globalThis);
